import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { PriceModelData } from '@store/models/Price';
import { priceService } from '@price/service/price/priceService';

import { useOrganizationOptionTypes } from '@organization/hooks/organization/useOrganizationOptionTypes';
import { parseAntdOptionTypeToOptionType } from '@utils/makeOptionTypes';
import { CommonOptionType } from '@utils/CommonOptionType';
import { OptionType } from '@components/Select';
import { PriceValue } from '@common/CreationMenuFields/BasePriceEditor/BasePriceEditor';
import { store } from '@store';

interface Props {
    value: PriceValue;
    onChange(params: Partial<PriceModelData>[]): void;
}

interface Data {
    basePrices: PriceModelData[];
    currencyOptions: OptionType<string>[];
    contractorOptions: OptionType<string>[];
    onItemChange(params: Partial<PriceModelData>, index: number): void;
    onDelete(index: number): void;
}

export function useBasePriceEditor({ onChange, value }: Props): Data {
    const [basePrices, setBasePrices] = useState<PriceModelData[]>([]);

    useEffect(() => {
        const serviceId = value.serviceId;
        if (!serviceId) {
            setBasePrices([]);
            return;
        }

        let cancelled = false;

        (async () => {
            const { priceStore } = store;
            await priceService.loadPriceByServiceIds([serviceId]);

            if (cancelled) return;
            setBasePrices(priceStore.findBasePrices(serviceId));
        })();

        return () => {
            cancelled = true;
        };
    }, [value.serviceId]);

    const { contractorOptions } = useOrganizationOptionTypes();
    const currencyOptions = CommonOptionType.getCurrencyOptions();

    function onItemChange(params: Partial<PriceModelData>, index: number): void {
        const newState: PriceModelData[] = [];
        const id = params.id ?? uuid();

        setBasePrices(prevState => {
            newState.push(...prevState);
            newState[index] = { ...prevState[index], ...params, id };
            return newState;
        });

        onChange(newState);
    }

    function onDelete(index: number): void {
        const newState: PriceModelData[] = [];

        setBasePrices(prevState => {
            newState.push(...prevState.slice(0, index), ...prevState.slice(index + 1, prevState.length));

            return newState;
        });

        onChange(newState);
    }

    return {
        basePrices,
        currencyOptions,
        contractorOptions: parseAntdOptionTypeToOptionType(contractorOptions),
        onItemChange,
        onDelete,
    };
}
